import * as React from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/cargofl-ui';

/**
 * "Are you sure" for the actions that take effect the instant they are clicked.
 *
 * Vendored from the CargoFL Performance design system, unchanged, so a confirmation
 * looks and behaves the same across all three applications.
 *
 * The bar for putting one in front of an action is narrow on purpose, because a
 * confirm on everything is a confirm nobody reads. It is worth it when the action is
 * **immediate and not undone by clicking again**: submitting a week locks it and
 * mails an approver, disabling somebody ends their access, deleting a tag strips it
 * off entries that already have it. Undoing any of those is a second deliberate act,
 * sometimes by a different person.
 *
 * It is NOT worth it for anything reversible in place - editing a cell, saving the
 * week grid, renaming a project, hiding a project (which unhides by clicking again) -
 * or for anything that already opens a dialog to collect a reason, which is its own
 * confirmation step. That is why denying a week has no confirm: it asks for the
 * reason first.
 *
 * The description says what will happen in the world, not what the button is called.
 * "This cannot be undone" is not a description; "the week is locked and Bilal Shaikh
 * is emailed" is.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive = false,
  pending = false,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  /** Red confirm button. For actions that remove or shut something. */
  destructive?: boolean;
  pending?: boolean;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          {/* Cancel first and Confirm last, and Cancel is the plain one: the safe
              choice should never be the one styled to be pressed. */}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            disabled={pending}
            onClick={onConfirm}
          >
            {pending ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A button that asks first.
 *
 * The common shape: one button, one action, one question. Where the trigger is not a
 * button — a dropdown selection that fires immediately, say — drive `ConfirmDialog`
 * directly from state instead.
 */
export function ConfirmButton({
  title,
  description,
  confirmLabel,
  destructive,
  pending,
  onConfirm,
  children,
  ...buttonProps
}: {
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  pending?: boolean;
  onConfirm: () => void;
  children: React.ReactNode;
} & Omit<React.ComponentProps<typeof Button>, 'onClick'>) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Button {...buttonProps} onClick={() => setOpen(true)}>
        {children}
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={title}
        description={description}
        confirmLabel={confirmLabel}
        destructive={destructive}
        pending={pending}
        onConfirm={() => {
          // Closed before the work starts, not after: these actions all report their
          // own outcome in a toast, and a dialog sitting open behind a toast reads as
          // "it did not take".
          setOpen(false);
          onConfirm();
        }}
      />
    </>
  );
}
